import React, { useEffect, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { isMuted, toggleMuted, playCue } from "@/lib/soundCues";

/**
 * <SoundToggle /> — one-tap global mute for the ambient cue system.
 * Persists via localStorage; broadcasts a `mwq-mute-changed` event so
 * any other component (HUD indicators, in-app settings) can react.
 * Renders as a small pill so it fits inside header rows without
 * dominating the layout.
 */
export default function SoundToggle({ className = "", size = "sm" }) {
  const [muted, setMutedState] = useState(() => isMuted());

  useEffect(() => {
    const onChange = (e) => setMutedState(!!e.detail);
    window.addEventListener("mwq-mute-changed", onChange);
    return () => window.removeEventListener("mwq-mute-changed", onChange);
  }, []);

  const handle = () => {
    const next = toggleMuted();
    setMutedState(next);
    // Play a tiny confirmation tick when UNmuting so the child gets
    // instant feedback that sound is back.
    if (!next) playCue("tap");
  };

  // G2 · 44px minimum hit-area even when the visual "sm" is compact.
  const dim = size === "sm" ? "h-11 w-11" : "h-12 w-12";
  const icon = size === "sm" ? 14 : 18;

  return (
    <button
      type="button"
      onClick={handle}
      data-testid="sound-toggle"
      aria-pressed={muted ? "false" : "true"}
      aria-label={muted ? "Turn sound on" : "Turn sound off"}
      title={muted ? "Sound off" : "Sound on"}
      className={`${dim} inline-flex items-center justify-center rounded-full border-[2.5px] border-slate-900 ${muted ? "bg-slate-200" : "bg-yellow-300"} shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:-translate-y-[1px] hover:shadow-[0_3px_0_0_rgba(15,23,42,1)] transition-all ${className}`}
    >
      {muted ? <VolumeX size={icon} strokeWidth={3} /> : <Volume2 size={icon} strokeWidth={3} />}
    </button>
  );
}
